import { useState } from "react";
import { Clock, Mail, MapPin, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { BUDGET_OPTIONS, ENTITIES, SERVICES } from "@/data/content";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-surface px-4 py-3 text-sm text-white placeholder:text-dim outline-none transition-colors focus:border-brand";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", service: "Not sure yet", budget: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Your name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "A valid email is required.";
    if (form.message.trim().length < 10) errs.message = "Tell us a little more (10+ characters).";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await api.post("/enquiries", { ...form, kind: "enquiry" });
      toast.success("Enquiry received — expect a reply within one business day.");
      setForm({ name: "", email: "", company: "", service: "Not sure yet", budget: "", message: "" });
      setErrors({});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact — Vigarbiz"
        siteName="Vigarbiz"
        description="Start a project with Vigarbiz. Offices in Pune, India and Fremont, USA. Replies within one business day."
      />
      <PageHeader
        overline="Contact"
        lines={["Tell us where", "You want to", <span key="b" className="text-brand">Be.</span>]}
        description="One form. One business day. One honest read on whether we're the right machine for your growth target."
      />

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:gap-16">
          <Reveal>
            <div className="space-y-6">
              {ENTITIES.map((e) => (
                <div
                  key={e.key}
                  data-testid={`contact-office-${e.key}`}
                  className="glow-card rounded-2xl border border-white/10 bg-surface p-6 sm:p-8"
                >
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand">{e.city}</p>
                  <h3 className="mt-2 font-display text-xl font-bold text-white">{e.name}</h3>
                  <div className="mt-4 space-y-2.5 text-sm text-muted2">
                    <p className="flex items-start gap-2">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                      {e.address}
                    </p>
                    <p className="flex items-center gap-2">
                      <Mail className="h-4 w-4 shrink-0 text-brand" />
                      <a href={`mailto:${e.email}`} className="transition-colors hover:text-white">
                        {e.email}
                      </a>
                    </p>
                    <p className="flex items-start gap-2">
                      <Phone className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                      <span>
                        {e.phones.map((p, i) => (
                          <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="transition-colors hover:text-white">
                            {p}
                            {i < e.phones.length - 1 ? " · " : ""}
                          </a>
                        ))}
                      </span>
                    </p>
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-3 rounded-2xl border border-volt/30 bg-volt/5 p-5 text-sm text-volt">
                <Clock className="h-5 w-5 shrink-0" />
                Follow-the-sun team — someone is awake in your timezone right now.
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-2xl border border-white/10 bg-surface p-6 sm:p-10"
              noValidate
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.15em] text-muted2">
                    Name *
                  </label>
                  <input id="contact-name" data-testid="contact-name-input" className={inputCls} placeholder="Alex Rivera" value={form.name} onChange={set("name")} />
                  {errors.name && <p data-testid="contact-name-error" className="mt-1.5 text-xs text-red-400">{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.15em] text-muted2">
                    Work email *
                  </label>
                  <input id="contact-email" type="email" data-testid="contact-email-input" className={inputCls} placeholder="alex@company.com" value={form.email} onChange={set("email")} />
                  {errors.email && <p data-testid="contact-email-error" className="mt-1.5 text-xs text-red-400">{errors.email}</p>}
                </div>
                <div>
                  <label htmlFor="contact-company" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.15em] text-muted2">
                    Company
                  </label>
                  <input id="contact-company" data-testid="contact-company-input" className={inputCls} placeholder="Acme Inc" value={form.company} onChange={set("company")} />
                </div>
                <div>
                  <label htmlFor="contact-budget" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.15em] text-muted2">
                    Monthly budget
                  </label>
                  <select id="contact-budget" data-testid="contact-budget-select" className={inputCls} value={form.budget} onChange={set("budget")}>
                    <option value="">Select a range</option>
                    {BUDGET_OPTIONS.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-service" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.15em] text-muted2">
                    What do you need?
                  </label>
                  <select id="contact-service" data-testid="contact-service-select" className={inputCls} value={form.service} onChange={set("service")}>
                    <option value="Not sure yet">Not sure yet</option>
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.title}>{s.title}</option>
                    ))}
                    <option value="Full-funnel retainer">Full-funnel retainer</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-message" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.15em] text-muted2">
                    The target *
                  </label>
                  <textarea id="contact-message" rows={5} data-testid="contact-message-input" className={inputCls} placeholder="Where are you now, where do you need to be, and by when?" value={form.message} onChange={set("message")} />
                  {errors.message && <p data-testid="contact-message-error" className="mt-1.5 text-xs text-red-400">{errors.message}</p>}
                </div>
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-form-submit-button"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand py-4 font-display text-base font-bold text-ink transition-colors duration-300 hover:bg-brand-hover active:scale-95 disabled:opacity-60 sm:w-auto sm:px-10"
              >
                {sending ? "Sending…" : "Send Enquiry"}
                <Send className="h-4 w-4" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
